/**
 * Centralized error message translations for availability checks
 *
 * Maps backend error codes to user-friendly translated messages
 * Supports Arabic (ar) and English (en)
 */

// Error codes - must match backend ERROR_CODES in availabilityEngine.js
export const ERROR_CODES = {
  EXISTING_BOOKING: 'EXISTING_BOOKING',
  // A relocated guest is physically inside this unit even though no booking names it
  // (tasks/guest-relocation-v1.md, L3). Non-overridable, like EXISTING_BOOKING.
  GUEST_IN_HOUSE: 'GUEST_IN_HOUSE',
  NO_INVENTORY: 'NO_INVENTORY',
  STOP_SELL: 'STOP_SELL',
  CLOSED_TO_ARRIVAL: 'CLOSED_TO_ARRIVAL',
  MIN_STAY: 'MIN_STAY',
  MAX_STAY: 'MAX_STAY',
  MISSING_DATA: 'MISSING_DATA',
  INVALID_DATE_RANGE: 'INVALID_DATE_RANGE',
  SYSTEM_ERROR: 'SYSTEM_ERROR'
};

/**
 * Get translated error message based on error code and language
 *
 * @param {string} errorCode - Error code from ERROR_CODES
 * @param {Object} errorData - Additional data for error message (e.g., minRequired, maxAllowed, date)
 * @param {string} lang - Language code ('ar' or 'en')
 * @returns {string} Translated error message
 */
export const getErrorMessage = (errorCode, errorData = {}, lang = 'ar') => {
  const messages = {
    ar: {
      EXISTING_BOOKING: `الوحدة غير متاحة في ${errorData.date || 'هذا التاريخ'}`,
      GUEST_IN_HOUSE: `يوجد ضيف داخل هذه الوحدة في ${errorData.date || 'هذا التاريخ'} (تم نقله إليها) — اختر وحدة أخرى`,
      NO_INVENTORY: `لا توجد غرف متاحة في ${errorData.date || 'هذا التاريخ'}`,
      STOP_SELL: `الحجز غير متاح في ${errorData.date || 'هذا التاريخ'}`,
      CLOSED_TO_ARRIVAL: `الوصول غير مسموح في ${errorData.date || 'هذا التاريخ'}`,
      MIN_STAY: `الحد الأدنى للإقامة هو ${errorData.minRequired || 'عدة'} ${(errorData.minRequired || 0) === 1 ? 'ليلة' : 'ليالي'}`,
      MAX_STAY: `الحد الأقصى للإقامة هو ${errorData.maxAllowed || 'عدة'} ${(errorData.maxAllowed || 0) === 1 ? 'ليلة' : 'ليالي'}`,
      MISSING_DATA: 'بيانات التوافر غير متاحة لهذا العقار. يرجى المحاولة مرة أخرى لاحقاً أو التواصل مع الدعم',
      INVALID_DATE_RANGE: 'التواريخ المدخلة غير صحيحة. يرجى التحقق من التواريخ والمحاولة مرة أخرى',
      SYSTEM_ERROR: 'حدث خطأ في النظام. يرجى المحاولة مرة أخرى لاحقاً',
      DEFAULT: 'هذه التواريخ غير متاحة'
    },
    en: {
      EXISTING_BOOKING: `Room already booked on ${errorData.date || 'this date'}`,
      GUEST_IN_HOUSE: `A relocated guest is staying in this unit on ${errorData.date || 'this date'} — choose another unit`,
      NO_INVENTORY: `No rooms available on ${errorData.date || 'this date'}`,
      STOP_SELL: `Booking not available on ${errorData.date || 'this date'}`,
      CLOSED_TO_ARRIVAL: `Arrival not allowed on ${errorData.date || 'this date'}`,
      MIN_STAY: `Minimum stay of ${errorData.minRequired || 'several'} ${(errorData.minRequired || 0) === 1 ? 'night' : 'nights'} required`,
      MAX_STAY: `Maximum stay of ${errorData.maxAllowed || 'several'} ${(errorData.maxAllowed || 0) === 1 ? 'night' : 'nights'} exceeded`,
      MISSING_DATA: 'Availability data not available for this property. Please try again later or contact support',
      INVALID_DATE_RANGE: 'Invalid date range. Please check your dates and try again',
      SYSTEM_ERROR: 'System error occurred. Please try again later',
      DEFAULT: 'These dates are not available'
    }
  };

  const langMessages = messages[lang] || messages.ar;
  return langMessages[errorCode] || langMessages.DEFAULT;
};

/**
 * Format blocked dates list for display
 *
 * @param {Array<string>} blockedDates - Array of blocked date strings (e.g., ['2025-10-16', '2025-10-17'])
 * @param {string} lang - Language code ('ar' or 'en')
 * @returns {string} Formatted date list string
 */
export const formatBlockedDates = (blockedDates, lang = 'ar') => {
  if (!blockedDates || blockedDates.length === 0) {
    return '';
  }

  // Format dates (remove year for cleaner display if current year)
  const currentYear = new Date().getFullYear();
  const formattedDates = blockedDates.map(dateStr => {
    const date = new Date(dateStr);
    const year = date.getFullYear();

    // Only show year if different from current year
    if (year !== currentYear) {
      return dateStr; // Keep full date format
    }

    // Remove year for cleaner display (e.g., "10-16" instead of "2025-10-16")
    return dateStr.substring(5); // Extract "MM-DD"
  });

  return formattedDates.join(', ');
};

/**
 * Get complete error message with blocked dates
 *
 * @param {string} errorCode - Error code from ERROR_CODES
 * @param {Object} errorData - Additional data for error message
 * @param {Array<string>} blockedDates - Array of blocked dates
 * @param {string} lang - Language code ('ar' or 'en')
 * @returns {string} Complete error message with suggestions
 */
export const getCompleteErrorMessage = (errorCode, errorData, blockedDates, lang = 'ar') => {
  let message = getErrorMessage(errorCode, errorData, lang);

  // Add blocked dates if available
  if (blockedDates && blockedDates.length > 0) {
    const dateList = formatBlockedDates(blockedDates, lang);

    if (lang === 'ar') {
      message += `\n\nالتواريخ غير المتاحة: ${dateList}`;
      message += `\n\nاختر تواريخ أخرى للتحقق من امكانية الحجز.`;
    } else {
      message += `\n\nUnavailable dates: ${dateList}`;
      message += `\n\nTry different dates to check availability.`;
    }
  }

  return message;
};

/**
 * Get structured error data for rendering with visual enhancements
 *
 * @param {string} errorCode - Error code from ERROR_CODES
 * @param {Object} errorData - Additional data for error message
 * @param {Array<string>} blockedDates - Array of blocked dates
 * @param {string} lang - Language code ('ar' or 'en')
 * @returns {Object} { message, blockedDates, suggestion } for custom rendering
 */
export const getErrorMessageWithDates = (errorCode, errorData, blockedDates, lang = 'ar') => {
  const message = getErrorMessage(errorCode, errorData, lang);
  const suggestion = lang === 'ar'
    ? 'اختر تواريخ أخرى للتحقق من امكانية الحجز.'
    : 'Try different dates to check availability.';

  return {
    message,
    blockedDates: blockedDates || [],
    suggestion: (blockedDates && blockedDates.length > 0) ? suggestion : null
  };
};
